import React, { useState, useRef } from 'react';
import { previewImport, executeImport, previewFirmImport, executeFirmImport } from '../utils/api';
import { canHover } from '../hooks/useBreakpoint';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

const FIRM_DB_FIELDS = [
  { value: '',                    label: '— ignore —' },
  { value: 'firm_name',           label: 'Firm Name' },
  { value: 'contact_name',        label: 'Contact Name' },
  { value: 'contact_title',       label: 'Contact Title' },
  { value: 'phone',               label: 'Phone Number' },
  { value: 'email',               label: 'Email' },
  { value: 'status',              label: 'Status' },
  { value: 'last_contacted',      label: 'Last Contacted' },
  { value: 'next_action',         label: 'Next Action' },
  { value: 'website',             label: 'Website' },
  { value: 'location',            label: 'Location' },
  { value: 'employee_count_range',label: '# Employees (range)' },
  { value: 'pain_points',         label: 'Pain Points' },
  { value: 'current_software',    label: 'Current Software' },
  { value: 'opportunity_notes',   label: 'Opportunity Notes' },
];

const DB_FIELDS = [
  { value: '',               label: '— ignore —' },
  { value: 'type',           label: 'Pipeline (Investor / Design Partner)' },
  { value: 'firm_name',      label: 'Firm Name / Investor' },
  { value: 'location',       label: 'Location' },
  { value: 'contact_name',   label: 'Primary Contact' },
  { value: 'email',          label: 'Email' },
  { value: 'phone',          label: 'Phone' },
  { value: 'linkedin',       label: 'LinkedIn' },
  { value: 'fund_type',      label: 'Type (Fund Type)' },
  { value: 'focus',          label: 'Focus' },
  { value: 'check_size',     label: 'Typical Check Size' },
  { value: 'fit_for_syncrea',label: 'Fit for Syncrea' },
  { value: 'notes',          label: 'Notes / Summary' },
  { value: 'priority',       label: 'Priority' },
  { value: 'stage',          label: 'Stage' },
  { value: 'status',         label: 'Status' },
  { value: 'owner',          label: 'Owner' },
  { value: 'segment',        label: 'Segment' },
  { value: 'website',        label: 'Website' },
  { value: 'firm_type',      label: 'Firm Type' },
  { value: 'managing_partner',label: 'Managing Partner' },
  { value: 'employee_count', label: '# Employees' },
  { value: 'client_count',   label: 'Est. Clients' },
  { value: 'cas_maturity',   label: 'CAS Maturity' },
  { value: 'uses_qbo',       label: 'Uses QBO' },
  { value: 'use_case',       label: 'Use Case' },
  { value: 'is_lead',        label: 'Lead Investor' },
  { value: 'activity',       label: 'Activity' },
  { value: 'activity_date',  label: 'Due Date' },
];

const overlay = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.7)',
  backdropFilter: 'blur(4px)',
  zIndex: 300,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20,
};

const modal = {
  background: 'var(--bg-card2)',
  border: '1px solid var(--border-med)',
  borderRadius: 14,
  width: '100%', maxWidth: 680,
  maxHeight: '90vh',
  display: 'flex', flexDirection: 'column',
  boxShadow: 'var(--shadow)',
};

const selStyle = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  color: 'var(--text)',
  padding: '4px 8px',
  fontSize: 12,
  fontFamily: 'DM Sans, sans-serif',
  cursor: 'pointer',
  outline: 'none',
  width: '100%',
};

export default function ImportModal({ pipeline, onDone, onClose }) {
  useBodyScrollLock();
  const isFirm = pipeline === 'Firm';
  const fieldOptions = isFirm ? FIRM_DB_FIELDS : DB_FIELDS;

  const [step,        setStep]        = useState('upload');
  const [preview,     setPreview]     = useState(null);
  const [mapping,     setMapping]     = useState({});
  const [duplicateMode, setDuplicateMode] = useState('skip');
  const [defaultType, setDefaultType] = useState('');
  const [result,      setResult]      = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [file,        setFile]        = useState(null);
  const fileRef = useRef();

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const data = isFirm ? await previewFirmImport(fd) : await previewImport(fd);
      setPreview(data);
      setMapping(data.suggestions || {});
      setStep('mapping');
    } catch (err) {
      setError(err.response?.data?.error || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mapping', JSON.stringify(mapping));
      fd.append('duplicateMode', duplicateMode);
      if (!isFirm) fd.append('defaultType', defaultType);
      const res = isFirm ? await executeFirmImport(fd) : await executeImport(fd);
      setResult(res);
      setStep('result');
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="import-overlay" style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="import-modal" style={modal}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, fontSize: 15 }}>
            ⬆ Import {isFirm ? 'Firms' : 'Contacts'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Body */}
        <div className="import-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {error && (
            <div style={{ background: 'var(--danger-bg)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, color: 'var(--danger)', padding: '8px 14px', fontSize: 12, marginBottom: 16 }}>
              ⚠ {error}
            </div>
          )}

          {/* STEP 1: Upload */}
          {step === 'upload' && (
            <div>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
                {isFirm
                  ? <>Upload a CSV or Excel file (.xlsx) to import firms. Only <strong>Firm Name</strong> is required. All other fields are optional.</>
                  : <>Upload a CSV or Excel file (.xlsx) to import records. Only <strong>Firm Name / Investor</strong> and <strong>Type</strong> are required. All other fields are optional.</>}
              </p>
              <div
                onClick={() => fileRef.current?.click()}
                style={{
                  border: '2px dashed var(--primary-border)',
                  borderRadius: 'var(--radius)',
                  padding: '40px 20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: 'var(--primary-dim)',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (canHover()) e.currentTarget.style.borderColor = 'var(--primary)'; }}
                onMouseLeave={e => { if (canHover()) e.currentTarget.style.borderColor = 'var(--primary-border)'; }}
              >
                <div style={{ fontSize: 32, marginBottom: 10 }}>📂</div>
                <div style={{ color: 'var(--primary)', fontWeight: 600, fontSize: 14 }}>Click to choose file</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>CSV or XLSX · max 10MB</div>
              </div>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} />
              {loading && <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 16, fontSize: 13 }}>Reading file…</div>}
            </div>
          )}

          {/* STEP 2: Column Mapping */}
          {step === 'mapping' && preview && (
            <div>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 4 }}>
                Map your file columns to Syncrea fields. <strong>{preview.totalRows} rows</strong> detected.
              </p>

              {!isFirm && !Object.values(mapping).includes('type') && (
                <div style={{ background: 'var(--warning-bg)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--warning)' }}>
                  ⚠ No "Pipeline" column mapped. Select a default pipeline for all rows:
                  <select style={{ ...selStyle, marginTop: 6 }} value={defaultType} onChange={e => setDefaultType(e.target.value)}>
                    <option value="">— select tracker —</option>
                    <option>Investor</option>
                    <option>Design Partner</option>
                  </select>
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                {preview.headers.map(h => (
                  <div key={h} className="import-map-row" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div className="import-map-label" style={{ width: 160, fontSize: 12, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={h}>
                      {h}
                    </div>
                    <div className="import-map-arrow" style={{ color: 'var(--text-dim)', fontSize: 14 }}>→</div>
                    <select
                      style={selStyle}
                      value={mapping[h] || ''}
                      onChange={e => setMapping(prev => ({ ...prev, [h]: e.target.value }))}
                    >
                      {fieldOptions.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
                  Duplicate Handling ({isFirm ? 'same Firm Name' : 'same Firm + Type'})
                </div>
                {['skip', 'update', 'import'].map(m => (
                  <label key={m} className="check-row" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>
                    <input type="radio" name="dupMode" value={m} checked={duplicateMode === m} onChange={() => setDuplicateMode(m)} />
                    <span>
                      {m === 'skip'   && 'Skip — keep existing record'}
                      {m === 'update' && 'Update — overwrite with import data'}
                      {m === 'import' && 'Import anyway — allow duplicates'}
                    </span>
                  </label>
                ))}
              </div>

              {preview.rows.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
                    Preview (first 5 rows)
                  </div>
                  <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                    <table style={{ fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
                      <thead>
                        <tr>
                          {preview.headers.map(h => (
                            <th key={h} style={{ padding: '6px 10px', background: 'var(--bg-card)', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', textAlign: 'left' }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((row, i) => (
                          <tr key={i}>
                            {preview.headers.map(h => (
                              <td key={h} style={{ padding: '5px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {String(row[h] ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Result */}
          {step === 'result' && result && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
              <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Import Complete</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Added',   val: result.added,   color: 'var(--success)' },
                  { label: 'Updated', val: result.updated, color: 'var(--warning)' },
                  { label: 'Skipped', val: result.skipped, color: 'var(--text-muted)' },
                ].map(s => (
                  <div key={s.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px' }}>
                    <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 28, fontWeight: 700, color: s.color }}>{s.val}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {result.errors?.length > 0 && (
                <div style={{ textAlign: 'left', background: 'var(--danger-bg)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--danger)' }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Issues:</div>
                  {result.errors.map((e, i) => <div key={i}>• {e}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="import-footer" style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {step === 'mapping' && (
            <>
              <button onClick={() => setStep('upload')} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-med)', borderRadius: 8, color: 'var(--text-muted)', fontFamily: 'DM Sans, sans-serif', fontSize: 13, padding: '8px 16px', cursor: 'pointer' }}>
                ← Back
              </button>
              <button
                onClick={handleImport}
                disabled={loading || (!isFirm && !Object.values(mapping).includes('type') && !defaultType)}
                style={{
                  background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
                  border: 'none', borderRadius: 8,
                  color: '#fff', fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 600, fontSize: 13, padding: '8px 24px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Importing…' : `Import ${preview?.totalRows} Rows`}
              </button>
            </>
          )}
          {step === 'result' && (
            <button onClick={() => onDone(result)} style={{ background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))', border: 'none', borderRadius: 8, color: '#fff', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13, padding: '8px 24px', cursor: 'pointer' }}>
              Done
            </button>
          )}
          {step !== 'result' && (
            <button onClick={onClose} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-med)', borderRadius: 8, color: 'var(--text-muted)', fontFamily: 'DM Sans, sans-serif', fontSize: 13, padding: '8px 16px', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}